import type {
  CriterionComparisonValueMode,
  ScorecardCompareOperator,
  ScorecardCompareType,
} from "../../../criterionConfig";
import CriterionComparisonValueField from "../Inputs/CriterionComparisonValueField";
import { COMPARE_OPERATOR_COPY, COMPARE_TYPE_COPY } from "../EditCriterionView.constants";
import { Field } from "../Layout/Field";

import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { SmartColumn } from "@/queries/smart-tables/types";

interface CompareFieldsProps {
  mode: CriterionComparisonValueMode;
  staticValue: string;
  columnId: string;
  columns: SmartColumn[];
  onModeChange: (mode: CriterionComparisonValueMode) => void;
  onStaticValueChange: (value: string) => void;
  onColumnChange: (columnId: string) => void;
  compareStaticPlaceholder: string;
  compareStaticDescription: string;
  hasMissingNumberStaticTarget: boolean;
  compareType: ScorecardCompareType;
  onCompareTypeChange: (type: ScorecardCompareType) => void;
  compareOperator: ScorecardCompareOperator;
  onCompareOperatorChange: (operator: ScorecardCompareOperator) => void;
  compareJsonPath: string;
  onCompareJsonPathChange: (path: string) => void;
  hasInvalidCompareConfig: boolean;
}

export const CompareFields = ({
  mode,
  staticValue,
  columnId,
  columns,
  onModeChange,
  onStaticValueChange,
  onColumnChange,
  compareStaticPlaceholder,
  compareStaticDescription,
  hasMissingNumberStaticTarget,
  compareType,
  onCompareTypeChange,
  compareOperator,
  onCompareOperatorChange,
  compareJsonPath,
  onCompareJsonPathChange,
  hasInvalidCompareConfig,
}: CompareFieldsProps) => (
  <>
    <CriterionComparisonValueField
      mode={mode}
      staticValue={staticValue}
      columnId={columnId}
      columns={columns}
      onModeChange={onModeChange}
      onStaticValueChange={onStaticValueChange}
      onColumnChange={onColumnChange}
      modeLabel="Target"
      staticModeLabel="Fixed value"
      columnModeLabel="Column"
      staticLabel="Target value"
      columnLabel="Target column"
      staticPlaceholder={compareStaticPlaceholder}
      staticDescription={compareStaticDescription}
      columnDescription="Uses each row's value from this column as the target."
    />
    {hasMissingNumberStaticTarget ? (
      <p className="text-xs text-amber-700">Enter a numeric target before saving.</p>
    ) : null}

    <Field label="Comparison type">
      <Select
        value={compareType}
        onValueChange={value => onCompareTypeChange(value as ScorecardCompareType)}
      >
        <SelectTrigger>
          <SelectValue placeholder="Select comparison type" />
        </SelectTrigger>
        <SelectContent>
          {(["STRING", "NUMBER", "JSON"] as const).map(type => (
            <SelectItem key={type} value={type}>
              {COMPARE_TYPE_COPY[type].label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">{COMPARE_TYPE_COPY[compareType].helper}</p>
    </Field>

    {compareType === "NUMBER" ? (
      <Field label="Operator">
        <Select
          value={compareOperator}
          onValueChange={value => onCompareOperatorChange(value as ScorecardCompareOperator)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select operator" />
          </SelectTrigger>
          <SelectContent>
            {(["EQ", "NE", "GT", "GTE", "LT", "LTE"] as const).map(operator => (
              <SelectItem key={operator} value={operator}>
                {COMPARE_OPERATOR_COPY[operator].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          {COMPARE_OPERATOR_COPY[compareOperator].helper}
        </p>
      </Field>
    ) : null}

    {compareType === "JSON" ? (
      <Field label="JSON path">
        <Input
          value={compareJsonPath}
          onChange={event => onCompareJsonPathChange(event.target.value)}
          placeholder="$.*"
          className="font-mono text-sm"
        />
        <p className="text-xs text-muted-foreground">
          Use the same JSON path for both columns before comparing values.
        </p>
        {hasInvalidCompareConfig ? (
          <p className="text-xs text-amber-700">
            JSON path is required when using JSON comparison.
          </p>
        ) : null}
      </Field>
    ) : null}
  </>
);
